import { useEffect, useState } from "react";
import { CreditCard, FileText, KeyRound, Loader2, Receipt, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Pagination, usePageSize } from "@/components/ui/pagination";

interface PaymentRow {
  id: string;
  created: string | null;
  amount: number;
  /** Partially refunded amount; fully refunded payments are not returned at all. */
  refunded: number;
  currency: string;
  kind: "subscription" | "listing_unlock" | "other";
  description: string;
  card_brand: string | null;
  card_last4: string | null;
  receipt_url: string | null;
  invoice_number: string | null;
  invoice_pdf: string | null;
}

interface PaymentHistoryResponse {
  configured: boolean;
  payments: PaymentRow[];
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const money = (amount: number, currency: string) =>
  amount.toLocaleString("en-US", { style: "currency", currency, minimumFractionDigits: 2 });

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const KIND_ICON = {
  subscription: RefreshCw,
  listing_unlock: KeyRound,
  other: CreditCard,
} as const;

/** The buyer's successful payments — the Payment History settings tab.
 *
 *  Read live from Stripe by /payments/history: failed, pending and fully
 *  refunded payments are filtered out server-side, so everything listed here
 *  is money that actually went through. */
export function PaymentHistoryCard() {
  const [data, setData] = useState<PaymentHistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Client-side paging: /payments/history returns the whole (capped) list in one
  // call, so flipping pages never goes back to Stripe.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize("paymentHistory.pageSize", PAGE_SIZE_OPTIONS, 10);

  useEffect(() => {
    api.get<PaymentHistoryResponse>("/payments/history")
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Could not load your payments."));
  }, []);

  if (error) {
    return (
      <div className="rounded-[1.3rem] border border-border/70 bg-card p-4 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your payments…
      </div>
    );
  }

  if (data.payments.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-[1.3rem] border border-dashed border-border/70 bg-card px-4 py-10 text-center">
        <Receipt className="h-8 w-8 text-muted-foreground/60" />
        <div className="text-sm font-semibold text-foreground">No payments yet</div>
        <p className="max-w-sm text-xs text-muted-foreground">
          Subscription charges and paid listing unlocks will show up here once they go through.
        </p>
      </div>
    );
  }

  const currency = data.payments[0].currency;
  const total = data.payments.reduce((sum, p) => sum + p.amount - p.refunded, 0);
  const totalPages = Math.max(1, Math.ceil(data.payments.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = data.payments.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[1.3rem] border border-primary/25 bg-primary/10 px-4 py-3">
        <div className="text-sm text-foreground/90">
          {data.payments.length.toLocaleString()} successful payment{data.payments.length === 1 ? "" : "s"}
        </div>
        <div className="text-sm text-foreground/90">
          Total paid <span className="ml-1 text-base font-bold text-foreground">{money(total, currency)}</span>
        </div>
      </div>

      <ul className="divide-y divide-border/70 overflow-hidden rounded-[1.3rem] border border-border/70 bg-card">
        {visible.map((p) => {
          const Icon = KIND_ICON[p.kind] ?? CreditCard;
          return (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">{p.description}</div>
                  <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                    <span>{formatDate(p.created)}</span>
                    {p.card_last4 && (
                      <span>· {p.card_brand ? titleCase(p.card_brand) : "Card"} ···· {p.card_last4}</span>
                    )}
                    {p.invoice_number && <span>· Invoice {p.invoice_number}</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm font-bold text-foreground">{money(p.amount, p.currency)}</div>
                  {p.refunded > 0 ? (
                    <div className="text-xs text-muted-foreground">{money(p.refunded, p.currency)} refunded</div>
                  ) : (
                    <div className="text-xs font-semibold text-emerald-500">Paid</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {/* The colour sits on an inner span: the unlayered `a { color: inherit }`
                      rule beats text-* utilities placed on the anchor itself. */}
                  {p.receipt_url && (
                    <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" title="View receipt"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border transition-colors hover:bg-muted">
                      <span className="text-primary"><Receipt className="h-4 w-4" /></span>
                    </a>
                  )}
                  {p.invoice_pdf && (
                    <a href={p.invoice_pdf} target="_blank" rel="noopener noreferrer" title="Download invoice PDF"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border transition-colors hover:bg-muted">
                      <span className="text-primary"><FileText className="h-4 w-4" /></span>
                    </a>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <Pagination
        page={currentPage}
        totalElements={data.payments.length}
        pageSize={pageSize}
        onChange={setPage}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
      />
    </div>
  );
}
